import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Plus, Pencil, Trash2, Store as StoreIcon, ShoppingBag } from "lucide-react";
import { toast } from "sonner";

function formatPrice(p, currency = "TRY") {
  try {
    return new Intl.NumberFormat("tr-TR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(p);
  } catch {
    return `${p} ${currency}`;
  }
}

export default function Store() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [params, setParams] = useSearchParams();
  const { user } = useAuth();
  const activeCat = params.get("kategori") || "";

  const load = async () => {
    setLoading(true);
    const [{ data: cats }, { data: prods }] = await Promise.all([
      api.get("/product-categories"),
      api.get("/products", { params: activeCat ? { category: activeCat } : {} }),
    ]);
    setCategories(cats);
    setProducts(prods);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeCat]);

  const setCat = (slug) => (slug ? setParams({ kategori: slug }) : setParams({}));

  const remove = async (id) => {
    if (!window.confirm("Bu ürünü silmek istediğine emin misin?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Ürün silindi");
      await load();
    } catch {
      toast.error("Silinemedi");
    }
  };

  return (
    <div className="k-container py-12 md:py-16">
      <div className="flex flex-col items-start justify-between gap-4 md:flex-row md:items-end">
        <div>
          <span className="k-tag mb-3 inline-flex">
            <StoreIcon className="mr-2 h-3.5 w-3.5" /> Mağaza
          </span>
          <h1 className="font-display text-6xl text-ink-900" data-testid="store-title">
            Küçük dükkân
          </h1>
          <p className="mt-2 max-w-2xl font-body text-ink-700">
            Ellerimizden çıkan kitaplar, malzeme kitleri, ahşap ve keçe oyuncaklar ile dijital kaynaklar.
          </p>
        </div>
        {user?.role === "admin" && (
          <div className="flex gap-2">
            <Link to="/admin/talepler" className="k-btn-secondary" data-testid="admin-inquiries-link">
              Talepler
            </Link>
            <Link to="/magaza/yeni" className="k-btn-primary" data-testid="new-product-btn">
              <Plus className="mr-2 h-4 w-4" /> Yeni ürün
            </Link>
          </div>
        )}
      </div>

      <div className="mt-8 flex flex-wrap gap-2" data-testid="store-category-filter">
        <button
          onClick={() => setCat("")}
          className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
            !activeCat
              ? "border-sage-500 bg-sage-500 text-cream-50"
              : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
          }`}
          data-testid="store-cat-all"
        >
          Tümü
        </button>
        {categories.map((c) => (
          <button
            key={c.slug}
            onClick={() => setCat(c.slug)}
            data-testid={`store-cat-${c.slug}`}
            className={`rounded-full border px-4 py-1.5 text-sm font-semibold transition-colors ${
              activeCat === c.slug
                ? "border-sage-500 bg-sage-500 text-cream-50"
                : "border-cream-300 bg-cream-50 text-ink-700 hover:border-sage-500/60"
            }`}
          >
            {c.name}
          </button>
        ))}
      </div>

      <div className="mt-10 grid gap-6 md:grid-cols-2 lg:grid-cols-3">
        {loading && [1, 2, 3].map((i) => <div key={i} className="k-card h-96 animate-pulse" />)}
        {!loading && products.length === 0 && (
          <div className="k-card p-10 text-center md:col-span-2 lg:col-span-3" data-testid="store-empty">
            <p className="font-body text-ink-700">
              {activeCat ? "Bu kategoride henüz ürün yok." : "Henüz ürün yok. Yakında minik bir dükkân açıyoruz 🌿"}
            </p>
          </div>
        )}
        {!loading &&
          products.map((p) => (
            <ProductCard
              key={p.product_id}
              p={p}
              onEdit={user?.role === "admin" ? () => (window.location.href = `/magaza/${p.product_id}/duzenle`) : null}
              onDelete={user?.role === "admin" ? () => remove(p.product_id) : null}
            />
          ))}
      </div>
    </div>
  );
}

function ProductCard({ p, onEdit, onDelete }) {
  const soldOut = p.product_type === "fiziksel" && !p.in_stock;
  const detailUrl = `/magaza/${p.slug || p.product_id}`;
  const canBuy = !soldOut && !!p.shopier_url;
  return (
    <div
      className="k-card group flex flex-col overflow-hidden p-0 transition-all hover:-translate-y-0.5 hover:shadow-warm"
      data-testid={`product-card-${p.product_id}`}
    >
      <Link to={detailUrl} className="relative block h-52 w-full overflow-hidden bg-earth-400/15" data-testid={`product-card-link-${p.product_id}`}>
        {p.image_url ? (
          <img
            src={p.image_url}
            alt={p.title}
            className="h-full w-full object-cover"
            onError={(e) => (e.currentTarget.style.display = "none")}
          />
        ) : (
          <div className="h-full w-full bg-gradient-to-br from-sage-500/20 to-earth-400/25" />
        )}
        <span
          className={`absolute left-3 top-3 rounded-full px-3 py-1 text-xs font-bold ${
            p.product_type === "dijital" ? "bg-sage-500 text-cream-50" : "bg-earth-400 text-cream-50"
          }`}
          data-testid={`product-type-${p.product_id}`}
        >
          {p.product_type === "dijital" ? "Dijital" : "Fiziksel"}
        </span>
        {p.product_type === "fiziksel" && (
          <span
            className={`absolute right-3 top-3 rounded-full px-3 py-1 text-xs font-bold ${
              soldOut ? "bg-destructive text-cream-50" : "bg-cream-50 text-sage-700"
            }`}
            data-testid={`product-stock-${p.product_id}`}
          >
            {soldOut ? "Tükendi" : "Stokta"}
          </span>
        )}
      </Link>
      <div className="flex flex-1 flex-col p-5">
        <Link to={detailUrl}>
          <h3 className="font-heading text-lg font-bold text-ink-900 transition-colors hover:text-sage-700">{p.title}</h3>
        </Link>
        <p className="mt-2 line-clamp-3 font-body text-sm text-ink-700">{p.description}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="font-heading text-2xl font-bold text-ink-900" data-testid={`product-price-${p.product_id}`}>
            {formatPrice(p.price, p.currency)}
          </span>
          {canBuy ? (
            <a
              href={p.shopier_url}
              target="_blank"
              rel="noopener noreferrer"
              className="k-btn-primary text-sm py-2 px-4"
              data-testid={`buy-btn-${p.product_id}`}
            >
              <ShoppingBag className="mr-1.5 h-3.5 w-3.5" /> Satın Al
            </a>
          ) : (
            <button
              disabled
              className="k-btn-primary text-sm py-2 px-4 opacity-50 cursor-not-allowed"
              data-testid={`buy-btn-${p.product_id}`}
            >
              {soldOut ? "Tükendi" : "Yakında"}
            </button>
          )}
        </div>
        {(onEdit || onDelete) && (
          <div className="mt-4 flex gap-2 border-t border-cream-300 pt-3 text-xs">
            {onEdit && (
              <button onClick={onEdit} className="k-btn-ghost !py-1 !px-2 text-ink-500" data-testid={`edit-product-${p.product_id}`}>
                <Pencil className="mr-1 h-3 w-3" /> Düzenle
              </button>
            )}
            {onDelete && (
              <button
                onClick={onDelete}
                className="k-btn-ghost !py-1 !px-2 text-destructive"
                data-testid={`delete-product-${p.product_id}`}
              >
                <Trash2 className="mr-1 h-3 w-3" /> Sil
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
